import { Button,Input } from 'components/atoms/index';
import * as styles from 'components/organisms/Table/Table.css'
import { Icon } from '@/icons';
import { vars } from 'style/theme.css'
import { useNavigate } from 'react-router-dom';
import Pagination from 'components/atoms/Pagination/Pagination';
import { useState } from 'react';
import { message } from 'antd';

interface AcademyExam {
    id: number
    name: string
    examStatus: '응시대기' | '응시완료' | '채점진행중' | '채점완료' | '채점불가' | '미응시' | '응시하기' | '채점중' | '기간만료' | '경고종료'
    examDate?: string
    score?: number
    examResultId?: number
  }
  
type AcademyExamTableProps = {
  data?: AcademyExam[];
  loading?: boolean;
  currentPage?: number;
  totalElements?: number;
  pageSize?: number;
  onPageChange?: (page: number) => void;
  searchText?: string;
  onSearchChange?: (text: string) => void;
  viewMode?: 'academy' | 'admin';
};

// 컬럼 렌더링용 메타 정보는 컴포넌트 내부에서 생성 (핸들러 접근을 위해)
  
  function renderAcademyStatus(status: AcademyExam['examStatus']) {
    switch (status) {
      case '응시대기':
        return <span className="font-medium text-gray-500">응시대기</span>
      case '응시완료':
        return <span className="font-medium text-blue-600">응시완료</span>
      case '채점진행중':
        return <span className="font-medium text-orange-600">채점진행중</span>
      case '채점완료':
        return <span className="font-medium text-green-600">채점완료</span>
      case '채점불가':
        return <span className="font-medium text-red-600">채점불가</span>
      case '경고종료':
        return <span className="font-medium text-red-600">경고종료</span>
      case '미응시':
        return <span className="font-medium text-gray-500">미응시</span>
      case '응시하기':
        return <span className="font-medium text-gray-500">응시대기</span>
      case '채점중':
        return <span className="font-medium text-orange-600">채점진행중</span>
      case '기간만료':
        return <span className="font-medium text-gray-500">기간만료</span>
      default:
        return <span>-</span>
    }
  }

// 더미 데이터 제거: 실제 데이터가 없을 때는 빈 상태로 표시

export default function AcademyExamTable({
  data,
  loading = false,
  currentPage = 1,
  totalElements = 0,
  pageSize = 10,
  onPageChange,
  searchText = '',
  onSearchChange,
  viewMode = 'academy',
}: AcademyExamTableProps) {
  const navigate = useNavigate();
  const [showOnlyAvailable, setShowOnlyAvailable] = useState(false);
  const baseDetailPath = viewMode === 'admin' ? '/admin/exam-result/detail' : '/academy/exam/result';

  const onClickScore = (row: AcademyExam, e: React.MouseEvent) => {
    e.stopPropagation();
    if (row.examStatus !== '채점완료') {
      const msg = row.examStatus === '응시대기' 
        ? '응시 전입니다.' 
        : row.examStatus === '응시완료'
          ? '채점 대기 중입니다.'
          : row.examStatus === '채점진행중'
            ? '채점 진행중입니다.'
            : row.examStatus === '채점불가'
              ? '경고 누적으로 채점이 불가합니다.'
              : row.examStatus === '경고종료'
                ? '경고 누적으로 자동 종료된 시험입니다. 채점 대상이 아닙니다.'
              : '아직 성적표를 볼 수 없습니다.';
      message.info(msg);
      return;
    }
    if (!row.examResultId) {
      message.error('시험 결과 ID가 없습니다.');
      return;
    }
    navigate(`${baseDetailPath}/${row.examResultId}`);
  };

  const handleRowClick = (row: AcademyExam) => {
    if (row.examStatus === '채점완료') {
      if (row.examResultId) {
        navigate(`${baseDetailPath}/${row.examResultId}`);
      } else {
        message.error('시험 결과 ID가 없습니다.');
      }
    } else {
      const msg = row.examStatus === '응시대기' 
        ? '응시 전입니다.' 
        : row.examStatus === '응시완료'
          ? '채점 대기 중입니다.'
          : row.examStatus === '채점진행중'
            ? '채점 진행중입니다.'
            : row.examStatus === '채점불가'
              ? '경고 누적으로 채점이 불가합니다.'
              : row.examStatus === '경고종료'
                ? '경고 누적으로 자동 종료된 시험입니다. 채점 대상이 아닙니다.'
              : '아직 성적표를 볼 수 없습니다.';
      message.info(msg);
    }
  };

  const handleToggleAvailable = () => {
    setShowOnlyAvailable(!showOnlyAvailable);
  };

  const columns = [
    { key: 'id'     as const, render: (row: AcademyExam) => row.id },
    { key: 'name'   as const, render: (row: AcademyExam) => row.name },
    { key: 'examStatus' as const, render: (row: AcademyExam) => renderAcademyStatus(row.examStatus) },
    { key: 'examDate' as const, render: (row: AcademyExam) => (
      <div className="flex gap-2 items-center lg:block">
        <span className="text-xs text-gray-400 lg:hidden">응시일</span>
        <span>{row.examDate || '-'}</span>
      </div>
    ) },
    { key: 'score' as const, render: (row: AcademyExam) => (
      <div className="flex flex-col gap-2 items-center">
        <Button 
          className='w-20' 
          size="small" 
          outline
          disabled={!(row.examStatus === '채점완료')}
          onClick={(e) => onClickScore(row, e)}
        >
          채점표
        </Button>
      </div>
    ) },
  ]

  return (
        <main className='flex flex-col w-full'>
            <section className='flex flex-col gap-2 items-start pt-3 w-full lg:flex-row lg:justify-between lg:items-center'>
                <Input
                placeholder='검색어를 입력해주세요.'
                className='max-w-[500px]'
                value={searchText}
                onChange={(e) => onSearchChange?.(e.target.value)}
                icon={<Icon name="search" size={20} color={vars.color.gray[300]} />}
                />
                <button 
                  className={`flex gap-1 justify-center items-center text-sm ${showOnlyAvailable ? 'text-indigo-500' : 'text-gray-500'}`}
                  onClick={handleToggleAvailable}
                >
                    <Icon name="check" size={20} className={showOnlyAvailable ? '*:fill-indigo-500' : '*:fill-gray-400'} />
                    <span>관리 가능한 시험만 보기</span>
                </button>
            </section>

            <section className='flex flex-col flex-1 justify-between pt-5 w-full'>
                {/* no_list */}
                {/* <p>리스트가 없습니다.</p> */}
                <div className='w-full'>
                    {/* 테이블 해더 pc 용*/}
                    <ul className={`${styles.academyHeaderList} w-full`}>
                        <li className={`${styles.headerItem} max-w-[80px] justify-center`}>순번</li>
                        <li className={`${styles.headerItem}`}>시험명</li>
                        <li className={`${styles.headerItem} max-w-[200px] lg:flex justify-center`}>응시상태</li>
                        <li className={`${styles.headerItem} max-w-[200px]`}>응시일</li>
                        <li className={`${styles.headerItem} max-w-[200px] lg:flex justify-center`}>채점표</li>
                    </ul>
                    {loading ? (
                      <div className='py-12 text-center text-gray-500'>로딩 중...</div>
                    ) : (data && data.length === 0) ? (
                      <div className='py-12 text-center text-gray-500'>등록된 시험이 없습니다.</div>
                    ) : (
                    <div className='flex flex-col gap-4 w-full lg:gap-0'>
                          {(data || []).map((row, index) => {
                            const totalCount = totalElements || (data ? data.length : 0);
                            const pageOffset = (currentPage - 1) * pageSize;
                            const descendingNo = totalCount > 0
                              ? totalCount - (pageOffset + index)
                              : Math.max(1, (data?.length ?? 0) - index);
                            return (
                            <ul 
                                key={row.id} 
                                className={`${styles.academyBodyList} lg:shadow-none table-card cursor-pointer hover:bg-gray-50 transition-colors !grid grid-cols-2 lg:!flex`}
                                onClick={() => handleRowClick(row)}
                            >
                            {columns.map(({ key, render }) => (
                                <li
                                key={key}
                                className={`${styles.bodyItem} ${
                                    key === 'id' ? 'max-w-[80px] !hidden lg:!flex justify-center' :
                                    key === 'name' ? 'col-span-2 lg:col-span-1' :
                                    key === 'examDate' ? 'text-sm text-gray-500 max-w-[200px] lg:static col-span-1' :
                                    key === 'examStatus' ? 'absolute top-5 right-5 lg:static max-w-[200px] lg:flex justify-center' :
                                    key === 'score' ? 'absolute top-5 right-5 lg:static max-w-[200px] lg:flex justify-center' :
                                    ''
                                }`}
                                >
                                {key === 'id' ? descendingNo : render(row)}
                                </li>
                            ))}
                            </ul>
                        )})}
                    </div>
                    )}
                </div>
                    <div className='py-5'>
                <Pagination
                total={totalElements || (data ? data.length : 0)}
                pageSize={pageSize}
                current={currentPage}
                onChange={(page) => onPageChange?.(page)}
                />
                </div>
            </section>
        </main>
  )
} 